/**
 * Web Push subscribe/unsubscribe utility.
 * Handles iOS Safari 16.4+ PWA-only push requirements explicitly.
 * Falls back gracefully when the browser or VAPID key is unavailable.
 */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

/** iOS Safari (iPhone / iPad) — includes iPadOS which reports as MacIntel. */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSAgent = /iPad|iPhone|iPod/i.test(ua);
  // iPadOS ≥ 13 pretends to be Mac; detect via touch + platform
  const iPadOSFake = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  return iOSAgent || iPadOSFake;
}

/** Is the app currently running as an installed PWA (Home Screen / standalone)? */
export function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  // iOS: navigator.standalone === true. Everyone else: matchMedia('(display-mode: standalone)')
  return (
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
  );
}

/** Fine-grained support probe. Returns { supported, reason } so the UI can guide the user. */
export function pushSupport() {
  if (typeof window === "undefined")
    return { supported: false, reason: "no_window" };
  if (!("serviceWorker" in navigator))
    return { supported: false, reason: "no_service_worker" };
  if (!("PushManager" in window))
    return { supported: false, reason: "no_push_manager" };
  if (!("Notification" in window))
    return { supported: false, reason: "no_notification_api" };
  if (isIOS() && !isStandalonePWA())
    return {
      supported: false,
      reason: "ios_needs_pwa_install",
      hint: "Tap the Share icon in Safari and choose 'Add to Home Screen', then open FanFest from your Home Screen to enable push.",
    };
  return { supported: true };
}

/** Legacy helper for callers that only care about the boolean. */
export function pushSupported() {
  return pushSupport().supported;
}

export async function currentPushSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Subscribe the current user to web push. Requests permission if needed,
 * fetches VAPID public key from backend, subscribes, and persists on server.
 * Returns {ok, reason?, hint?} — never throws.
 */
export async function subscribeToPush() {
  const probe = pushSupport();
  if (!probe.supported) {
    return { ok: false, reason: probe.reason, hint: probe.hint };
  }
  try {
    // ---- 1. Get or force-install a fresh service worker ----
    // iOS PWAs cache the SW aggressively; users on an old SW never get push.
    // We register (or re-register) explicitly and wait for `activated`.
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      reg = await navigator.serviceWorker.register("/service-worker.js");
    } else {
      // ask the browser to fetch the latest SW script
      try { await reg.update(); } catch (_) { /* update errors ignored */ }
    }
    // Wait for the SW to be active (iOS Safari sometimes stalls at `installing`)
    await new Promise((resolve) => {
      const done = () => resolve();
      if (reg.active && reg.active.state === "activated") return done();
      const listen = (worker) => {
        if (!worker) return;
        if (worker.state === "activated") return done();
        worker.addEventListener("statechange", () => {
          if (worker.state === "activated") done();
        });
      };
      listen(reg.active);
      listen(reg.installing);
      listen(reg.waiting);
      // Fallback: give up waiting after 10s and try anyway
      setTimeout(done, 10000);
    });

    // ---- 2. Request notification permission (user gesture required on iOS) ----
    const currentPerm = typeof Notification !== "undefined" ? Notification.permission : "default";
    let perm = currentPerm;
    if (perm !== "granted") {
      // iOS requires this to be from a direct user gesture — enablePush() button click is fine
      perm = await Notification.requestPermission();
    }
    if (perm !== "granted") {
      return {
        ok: false,
        reason: perm === "denied" ? "permission_denied" : "permission_dismissed",
        hint: perm === "denied"
          ? "You've blocked notifications for this app. On iPhone: Settings → Notifications → FanFest → Allow Notifications."
          : "Tap 'Allow' on the notification permission prompt.",
      };
    }

    // ---- 3. Subscribe with VAPID applicationServerKey ----
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get("/push/vapid-public-key");
      if (!data?.key || !data?.enabled) {
        return { ok: false, reason: "server_not_configured",
                 hint: "Push isn't configured on our servers yet. Please try again later." };
      }
      try {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.key),
        });
      } catch (e) {
        console.error("[push] subscribe() failed:", e);
        return {
          ok: false,
          reason: "subscribe_failed",
          hint: `Subscription failed (${e.name}: ${e.message}). Try removing the app from Home Screen and reinstalling.`,
        };
      }
    }

    // ---- 4. Persist to server ----
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: "invalid_subscription",
               hint: "Push subscription is missing keys. Reinstall the app to fix." };
    }
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    });
    return { ok: true, endpoint: json.endpoint };
  } catch (e) {
    console.error("[push] subscribeToPush error:", e);
    return { ok: false, reason: "unexpected", hint: e.message || "Something went wrong. Please try again." };
  }
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return { ok: true };
  try {
    const sub = await currentPushSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e.message };
  }
}

/** Rich diagnostics for the UI's "Why isn't push working?" panel. */
export async function pushDiagnostics() {
  const iso = new Date().toISOString();
  const result = {
    generated_at: iso,
    is_ios: isIOS(),
    is_standalone_pwa: isStandalonePWA(),
    service_worker_supported: "serviceWorker" in navigator,
    push_manager_supported: "PushManager" in window,
    notification_api_supported: "Notification" in window,
    notification_permission: typeof Notification !== "undefined" ? Notification.permission : "unavailable",
    user_agent: navigator.userAgent,
  };
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    result.service_worker_registered = !!reg;
    if (reg) {
      result.service_worker_scope = reg.scope;
      result.service_worker_state = reg.active?.state || reg.installing?.state || reg.waiting?.state || "none";
      const sub = await reg.pushManager?.getSubscription?.();
      result.has_push_subscription = !!sub;
      if (sub) {
        const json = sub.toJSON();
        result.subscription_endpoint = json.endpoint?.slice(0, 60) + "…";
        result.subscription_has_keys = !!(json.keys?.p256dh && json.keys?.auth);
      }
    }
  } catch (e) {
    result.error = e.message;
  }
  try {
    const { data } = await api.get("/push/vapid-public-key");
    result.server_push_enabled = !!data?.enabled;
    result.server_vapid_key_present = !!data?.key;
  } catch (e) {
    result.server_push_enabled = false;
    result.server_error = e.message;
  }
  return result;
}
